/**
 * What-If Simulator - Portfolio scenario analysis
 * Renders externally-provided scenarios or shows CTA to build custom ones
 * PRIMARY VALUE: IA testing allocation changes before execution
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Zap, Activity, TrendingDown, TrendingUp, CheckCircle } from 'lucide-react';

interface WhatIfSimulatorProps {
  onOpenComposer: () => void;
  currency?: string;
  scenarios?: Scenario[];
}

export interface Scenario {
  id: string;
  title: string;
  description: string;
  actions: string[];
  projectedMetrics: {
    volatility: number;
    var: number;
    sharpe: number;
  };
  currentMetrics?: {
    volatility: number;
    var: number;
    sharpe: number;
  };
  category: 'defensive' | 'growth' | 'rebalance';
}

const WhatIfSimulator: React.FC<WhatIfSimulatorProps> = ({
  onOpenComposer,
  currency = 'CHF',
  scenarios
}) => {
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const hasScenarios = scenarios && scenarios.length > 0;

  const toggleScenario = (scenarioId: string) => {
    setExpandedScenario(expandedScenario === scenarioId ? null : scenarioId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getImpactColor = (current: number, projected: number, lowerIsBetter: boolean = false) => {
    const change = projected - current;
    const isImprovement = lowerIsBetter ? change < 0 : change > 0;
    return isImprovement ? 'text-green-600' : 'text-red-600';
  };

  const getChangePercentage = (current: number, projected: number) => {
    if (current === 0) return 0;
    return ((projected - current) / current) * 100;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'defensive':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'growth':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rebalance':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Zap className="w-6 h-6 text-yellow-500" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">What-If Simulator</h3>
          <p className="text-sm text-gray-600">
            Test portfolio changes and see projected risk impact
          </p>
        </div>
      </div>

      {hasScenarios ? (
        /* Scenario cards */
        <div className="space-y-3 mb-4">
          {scenarios.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const current = scenario.currentMetrics || { volatility: 0, var: 0, sharpe: 0 };
            const volChange = getChangePercentage(current.volatility, scenario.projectedMetrics.volatility);
            const varChange = getChangePercentage(current.var, scenario.projectedMetrics.var);
            const sharpeChange = getChangePercentage(current.sharpe, scenario.projectedMetrics.sharpe);

            return (
              <div
                key={scenario.id}
                className="border border-gray-200 rounded-lg overflow-hidden hover:border-kielis-navy transition-colors"
              >
                {/* Scenario Header */}
                <button
                  onClick={() => toggleScenario(scenario.id)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded border text-xs font-medium ${getCategoryColor(scenario.category)}`}>
                      {getCategoryLabel(scenario.category)}
                    </span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{scenario.title}</div>
                      <div className="text-xs text-gray-600">{scenario.description}</div>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>

                {/* Scenario Details (Expandable) */}
                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    {/* Actions List */}
                    <div className="mb-4">
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Actions:</h5>
                      <ul className="space-y-1">
                        {scenario.actions.map((action, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Projected Impact */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3">Projected Impact:</h5>
                      <div className="grid grid-cols-3 gap-4">
                        {/* Volatility */}
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Volatility</div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-gray-900">
                              {scenario.projectedMetrics.volatility.toFixed(1)}%
                            </span>
                            {current.volatility > 0 && (
                              <>
                                <span className={`text-sm font-medium ${getImpactColor(current.volatility, scenario.projectedMetrics.volatility, true)}`}>
                                  {volChange > 0 ? '+' : ''}{volChange.toFixed(0)}%
                                </span>
                                {volChange < 0 ? (
                                  <TrendingDown className="w-4 h-4 text-green-600" />
                                ) : (
                                  <TrendingUp className="w-4 h-4 text-red-600" />
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* VaR */}
                        <div>
                          <div className="text-xs text-gray-600 mb-1">VaR (95%)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-gray-900">
                              {formatCurrency(scenario.projectedMetrics.var)}
                            </span>
                            {current.var > 0 && (
                              <span className={`text-sm font-medium ${getImpactColor(current.var, scenario.projectedMetrics.var, true)}`}>
                                {varChange > 0 ? '+' : ''}{varChange.toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Sharpe Ratio */}
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Sharpe Ratio</div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-gray-900">
                              {scenario.projectedMetrics.sharpe.toFixed(2)}
                            </span>
                            {current.sharpe > 0 && (
                              <>
                                <span className={`text-sm font-medium ${getImpactColor(current.sharpe, scenario.projectedMetrics.sharpe, false)}`}>
                                  {sharpeChange > 0 ? '+' : ''}{sharpeChange.toFixed(0)}%
                                </span>
                                {sharpeChange > 0 ? (
                                  <TrendingUp className="w-4 h-4 text-green-600" />
                                ) : (
                                  <TrendingDown className="w-4 h-4 text-red-600" />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-8 text-center mb-4">
          <Activity className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium mb-1">No pre-configured scenarios</p>
          <p className="text-sm text-gray-500">
            Use Portfolio Composer to build and test custom what-if scenarios
          </p>
        </div>
      )}

      {/* Custom Scenario Builder CTA */}
      <div className={hasScenarios ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
        <button
          onClick={onOpenComposer}
          className="w-full py-3 px-4 bg-gradient-to-r from-kielis-navy to-blue-900 hover:from-blue-900 hover:to-kielis-navy text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
        >
          <Activity className="w-5 h-5" />
          Build Custom Scenario
        </button>
        <p className="text-xs text-center text-gray-500 mt-2">
          Use Portfolio Composer to create your own what-if scenarios with buy, sell, and cash transactions
        </p>
      </div>
    </div>
  );
};

export default WhatIfSimulator;
